import type {
  IUserProfile,
  IUserFavoriteCategories,
  // IV2BonusBalanceResponse,
  IUserCardsResponse,
  IUserProfileResponse,
  ISendTotpResponse,
  IUserPermissionsResponse,
  IUserBalanceResponse,
  IUserBenefitsResponse,
  IUserNextStickerExpirationResponse,
} from "../typings";
import { useTransport } from "../composables/useTransport";
import useDirectFetch from "../composables/useDirectFetch";
import { userPermissionsApiKeyMap } from "../constants";
import { renameKeys } from "../utils/object";
import { urls } from "./config";

const getEnrollDeviceParams = (
  pushToken: string,
  notificationsPermitted: boolean,
  options = {},
) => ({
  method: "POST" as const,
  body: {
    push_token: pushToken,
    notifications_permitted: notificationsPermitted,
  },
  permissions: { jwt: true },
  ...options,
});
export const requestEnrollDevice = (
  pushToken: string,
  notificationsPermitted: boolean,
  options = {},
) => {
  return useTransport(urls.user.enrollDevice, getEnrollDeviceParams(pushToken, notificationsPermitted, options));
};
export const fetchEnrollDevice = (
  pushToken: string,
  notificationsPermitted: boolean,
  options = {},
) => {
  return useDirectFetch(urls.user.enrollDevice, getEnrollDeviceParams(pushToken, notificationsPermitted, options));
};

const getUserParams = (options = {}) => ({
  permissions: {
    jwt: true,
  },
  ...options,
});
export const requestFavoriteCategories = (options = {}) => {
  return useTransport<IUserFavoriteCategories>(urls.user.favoriteCategories, getUserParams(options));
};
export const fetchFavoriteCategories = (options = {}) => {
  return useDirectFetch<IUserFavoriteCategories>(urls.user.favoriteCategories, getUserParams(options));
};

const getUpdFavoriteSectionCategoriesParams = (codes: string[], options = {}) => ({
  method: "POST" as const,
  body: {
    codes,
  },
  permissions: {
    jwt: true,
  },
  ...options,
});
export const requestUpdFavoriteSectionCategories = (codes: string[], options = {}) => {
  return useTransport(urls.user.favoriteCategoriesOptIn, getUpdFavoriteSectionCategoriesParams(codes, options));
};
export const fetchUpdFavoriteSectionCategories = (codes: string[], options = {}) => {
  return useDirectFetch(urls.user.favoriteCategoriesOptIn, getUpdFavoriteSectionCategoriesParams(codes, options));
};

export const requestUpdFavoriteCategories = (codes: string[], options = {}) => {
  return useTransport(urls.user.favoriteCategories, getUpdFavoriteSectionCategoriesParams(codes, options));
};
export const fetchUpdFavoriteCategories = (codes: string[], options = {}) => {
  return useDirectFetch(urls.user.favoriteCategories, getUpdFavoriteSectionCategoriesParams(codes, options));
};

export const requestUserBalance = (options = {}) => {
  return useTransport<IUserBalanceResponse>(`${urls.user.balance}`, getUserParams(options));
};
export const fetchUserBalance = (options = {}) => {
  return useDirectFetch<IUserBalanceResponse>(`${urls.user.balance}`, getUserParams(options));
};

export const requestUserBenefits = (options = {}) => {
  return useTransport<IUserBenefitsResponse>(`${urls.user.benefits}?device_date=${Date.now()}`, getUserParams(options));
};
export const fetchUserBenefits = (options = {}) => {
  return useDirectFetch<IUserBenefitsResponse>(`${urls.user.benefits}?device_date=${Date.now()}`, getUserParams(options));
};

export const requestUserNextStickerExpiration = (options = {}) => {
  return useTransport<IUserNextStickerExpirationResponse>(`${urls.user.nextStickerExpiration}`, getUserParams(options));
};
export const fetchUserNextStickerExpiration = (options = {}) => {
  return useDirectFetch<IUserNextStickerExpirationResponse>(`${urls.user.nextStickerExpiration}`, getUserParams(options));
};

export const requestCards = (options = {}) => {
  return useTransport<IUserCardsResponse>(urls.user.cards, getUserParams(options));
};
export const fetchCards = (options = {}) => {
  return useDirectFetch<IUserCardsResponse>(urls.user.cards, getUserParams(options));
};

const getMergeCardsParams = (cvv: string, identifier: string, token: string, options = {}) => ({
  method: "POST" as const,
  body: {
    additionalIdentifierCvv: cvv,
    additionalIdentifierNo: identifier,
    card_type: "loyalty",
  },
  permissions: {
    jwt: true,
  },
  headers: {
    "X-Captcha-Token": token,
  },
  ...options,
});
export const requestMergeCards = (cvv: string, identifier: string, token: string, options = {}) => {
  return useTransport(urls.user.cardMerge, getMergeCardsParams(cvv, identifier, token, options));
};
export const fetchMergeCards = (cvv: string, identifier: string, token: string, options = {}) => {
  return useDirectFetch(urls.user.cardMerge, getMergeCardsParams(cvv, identifier, token, options));
};

const getRegisterParams = (body: {
  magnitIDCode: string;
  firstName: string;
  birthDate?: string;
  email?: string;
}, options = {}) => ({
  gateway: "magnit-id" as const,
  method: "POST" as const,
  body,
  ...options,
});
export const requestRegister = (requestData: {
  magnitIDCode: string;
  firstName: string;
  birthDate?: string;
  email?: string;
}, options = {}) => {
  return useTransport<IUserProfileResponse>(urls.user.register, getRegisterParams(requestData, options));
};
export const fetchRegister = (requestData: {
  magnitIDCode: string;
  firstName: string;
  birthDate?: string;
  email?: string;
}, options = {}) => {
  return useDirectFetch<IUserProfileResponse>(urls.user.register, getRegisterParams(requestData, options));
};

const getCheckUserProfileParams = () => ({
  method: "GET" as const,
  permissions: {
    jwt: true,
  },
});
export const requestCheckUserProfile = () => {
  return useTransport<IUserProfileResponse>(urls.user.checkUserProfile, getCheckUserProfileParams());
};
export const fetchCheckUserProfile = () => {
  return useDirectFetch<IUserProfileResponse>(urls.user.checkUserProfile, getCheckUserProfileParams());
};

const getProfileParams = (options = {}) => ({
  gateway: "magnit-id" as const,
  permissions: {
    jwt: true,
  },
  ...options,
});
export const requestProfile = (options = {}) => {
  return useTransport<IUserProfileResponse>(urls.user.profile, getProfileParams(options));
};
export const fetchProfile = (options = {}) => {
  return useDirectFetch<IUserProfileResponse>(urls.user.profile, getProfileParams(options));
};

const getPermissionsParams = (options = {}) => ({
  permissions: {
    jwt: true,
  },
  ...options,
});
export const requestPermissions = (options = {}) => {
  return useTransport<IUserPermissionsResponse>(urls.user.permissions, getPermissionsParams(options));
};
export const fetchPermissions = (options = {}) => {
  return useDirectFetch<IUserPermissionsResponse>(urls.user.permissions, getPermissionsParams(options));
};

const getUpdatePermissionsParams = (patch: Partial<IUserPermissionsResponse>, options = {}) => ({
  method: "PUT" as const,
  body: renameKeys(patch, userPermissionsApiKeyMap, true),
  permissions: {
    jwt: true,
  },
  ...options,
});
export const requestUpdatePermissions = (patch: Partial<IUserPermissionsResponse>, options = {}) => {
  return useTransport<IUserPermissionsResponse>(urls.user.permissions, getUpdatePermissionsParams(patch, options));
};
export const fetchUpdatePermissions = (patch: Partial<IUserPermissionsResponse>, options = {}) => {
  return useDirectFetch<IUserPermissionsResponse>(urls.user.permissions, getUpdatePermissionsParams(patch, options));
};

const getUpdateProfileParams = (patch: Partial<Record<keyof IUserProfile, string>>, options = {}) => ({
  gateway: "magnit-id" as const,
  method: "PATCH" as const,
  body: patch,
  permissions: {
    jwt: true,
  },
  ...options,
});
export const requestUpdateProfile = (patch: Partial<Record<keyof IUserProfile, string>>, options = {}) => {
  return useTransport<IUserProfileResponse>(urls.user.profile, getUpdateProfileParams(patch, options));
};
export const fetchUpdateProfile = (patch: Partial<Record<keyof IUserProfile, string>>, options = {}) => {
  return useDirectFetch<IUserProfileResponse>(urls.user.profile, getUpdateProfileParams(patch, options));
};

const getConfirmEmailParams = (email: string, options = {}) => ({
  method: "POST" as const,
  gateway: "magnit-id" as const,
  body: { email },
  permissions: {
    jwt: true,
  },
  ...options,
});
export const requestConfirmEmail = (email: string, options = {}) => {
  return useTransport(urls.user.emailConfirmation, getConfirmEmailParams(email, options));
};
export const fetchConfirmEmail = (email: string, options = {}) => {
  return useDirectFetch(urls.user.emailConfirmation, getConfirmEmailParams(email, options));
};

const getQrParams = (options = {}) => ({
  permissions: {
    jwt: true,
  },
  ...options,
});
export const requestQr = (options = {}) => {
  return useTransport<ISendTotpResponse>(urls.user.qrcode, getQrParams(options));
};
export const fetchQr = (options = {}) => {
  return useDirectFetch<ISendTotpResponse>(urls.user.qrcode, getQrParams(options));
};
